// Make changes › Checks — sentences ⇄ the designer's ValidationRule, in the
// engine's own expression language ({form.x}, not_empty(), today(), len(),
// == / != / > …) or its builtins (value_in_range, must_be_unique,
// child_form_has_entry). The full designer opens every result unchanged; a
// rule these sentences cannot read back stays as it is and is shown as a
// read-only line (`validationSummary`).
//
// Two checks belong to the field's Rules tab, not its Checks list: "No two
// records the same" (must_be_unique) and "Smallest / Largest"
// (value_in_range). They are recognised by their name suffix so the Rules
// switches find them again.

import type { BuiltinValidation, Validation, ValidationRule, ValidationSeverity } from '../../txn/types'
import { TEXT_LIT, quoteText, unquoteText } from './itemChange'

export type CompareOp = '>' | '>=' | '<' | '<=' | '=='

export const COMPARE_OPS: { op: CompareOp; label: string }[] = [
  { op: '>', label: 'more than' },
  { op: '>=', label: 'at least' },
  { op: '<', label: 'less than' },
  { op: '<=', label: 'no more than' },
  { op: '==', label: 'the same as' },
]

export type CheckSentence =
  | { type: 'range'; a: string; b: string }
  | { type: 'future' }
  | { type: 'reqwhen'; field: string; value: string }
  | { type: 'compare'; op: CompareOp; field: string }
  | { type: 'len'; n: number }

export interface FieldCheck {
  sentence: CheckSentence
  message: string
  severity: 'E' | 'W'
  /** The rule's name (kept on rewrite so warnings stay acknowledged). */
  name: string
}

export const UNIQUE_SUFFIX = '__unique'
export const LIMITS_SUFFIX = '__limits'

const IDENT = '[A-Za-z_][A-Za-z0-9_]*'
const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
const ref = (n: string) => `{form.${n}}`
const unquote = unquoteText

export function isRule(v: Validation): v is ValidationRule {
  return !!v && typeof v === 'object' && typeof (v as ValidationRule).type === 'string'
}

export function checkExpression(self: string, s: CheckSentence): string | null {
  switch (s.type) {
    case 'future': return `${ref(self)} <= today()`
    case 'reqwhen': return `not_empty(${ref(self)}) or ${ref(s.field)} != ${quoteText(s.value)}`
    case 'compare': return `${ref(self)} ${s.op} ${ref(s.field)}`
    case 'len': return `len(trim(${ref(self)})) >= ${Math.max(0, Math.round(Number(s.n) || 0))}`
    default: return null
  }
}

/** A field check as the designer's ValidationRule. */
export function checkToRule(self: string, c: FieldCheck): ValidationRule {
  const base = { name: c.name, message: c.message, severity: c.severity as ValidationSeverity }
  if (c.sentence.type === 'range') {
    return {
      ...base, type: 'builtin', skip_if_blank: true,
      builtin: {
        kind: 'value_in_range',
        min: { source: 'const', value: c.sentence.a },
        max: { source: 'const', value: c.sentence.b },
      },
    }
  }
  // A blank value is "must be filled in"'s business, never these checks' —
  // except "filled in when…", which is about blanks.
  return {
    ...base, type: 'expression', expression: checkExpression(self, c.sentence) || '',
    ...(c.sentence.type === 'reqwhen' ? {} : { skip_if_blank: true }),
  }
}

/** Read one validation back into a field check; null when it is not one of
 *  these sentences (shown read-only). */
export function ruleToCheck(self: string, v: Validation): FieldCheck | null {
  if (!isRule(v) || v.disabled) return null
  if (v.name?.endsWith(UNIQUE_SUFFIX) || v.name?.endsWith(LIMITS_SUFFIX)) return null
  const severity: 'E' | 'W' = v.severity === 'W' ? 'W' : 'E'
  const mk = (sentence: CheckSentence): FieldCheck => ({ sentence, message: v.message || '', severity, name: v.name })
  if (v.type === 'builtin' && v.builtin?.kind === 'value_in_range'
      && v.builtin.min?.source === 'const' && v.builtin.max?.source === 'const') {
    return mk({ type: 'range', a: String(v.builtin.min.value), b: String(v.builtin.max.value) })
  }
  if (v.type !== 'expression') return null
  const e = String(v.expression || '').trim()
  const me = esc(ref(self))
  if (new RegExp(`^${me}\\s*<=\\s*today\\(\\)$`).test(e)) return mk({ type: 'future' })
  let m = e.match(new RegExp(`^not_empty\\(${me}\\)\\s+or\\s+\\{form\\.(${IDENT})\\}\\s*!=\\s*(${TEXT_LIT})$`))
  if (m && unquote(m[2]) !== null) return mk({ type: 'reqwhen', field: m[1], value: unquote(m[2])! })
  m = e.match(new RegExp(`^${me}\\s*(>=|<=|==|>|<)\\s*\\{form\\.(${IDENT})\\}$`))
  if (m) return mk({ type: 'compare', op: m[1] as CompareOp, field: m[2] })
  m = e.match(new RegExp(`^len\\(trim\\(${me}\\)\\)\\s*>=\\s*(\\d+)$`))
  if (m) return mk({ type: 'len', n: Number(m[1]) })
  return null
}

/** A rule name not used yet in `validations`. */
export function uniqueRuleName(base: string, validations: Validation[] | undefined): string {
  const taken = new Set((validations || []).filter(isRule).map((v) => v.name))
  let name = base
  for (let n = 2; taken.has(name); n++) name = `${base}_${n}`
  return name
}

/** The message a check shows when nobody wrote one. */
export function defaultCheckMessage(label: string, s: CheckSentence, fieldLabel: (n: string) => string): string {
  switch (s.type) {
    case 'range': return `${label} must be between ${s.a || '…'} and ${s.b || '…'}`
    case 'future': return `${label} can’t be in the future`
    case 'reqwhen': return `Fill in ${label} when ${s.field ? fieldLabel(s.field) : '…'} is ${s.value || '…'}`
    case 'compare': return `${label} must be ${COMPARE_OPS.find((o) => o.op === s.op)?.label ?? s.op} ${s.field ? fieldLabel(s.field) : '…'}`
    case 'len': return `${label} needs at least ${s.n || '…'} letters`
  }
}

export function checkText(label: string, s: CheckSentence, fieldLabel: (n: string) => string): string {
  switch (s.type) {
    case 'range': return `${label} is between ${s.a} and ${s.b}`
    case 'future': return `${label} is not after today`
    case 'reqwhen': return `${label} is filled in when ${fieldLabel(s.field)} is “${s.value}”`
    case 'compare': return `${label} is ${COMPARE_OPS.find((o) => o.op === s.op)?.label ?? s.op} ${fieldLabel(s.field)}`
    case 'len': return `${label} has at least ${s.n} letters`
  }
}

// ── the Rules tab's own checks ───────────────────────────────────────────

/** "No two records the same": a must_be_unique builtin on the field. */
export function uniqueRule(self: string, label: string): ValidationRule {
  return {
    type: 'builtin', name: `${self}${UNIQUE_SUFFIX}`, severity: 'E', skip_if_blank: true,
    message: `Another record already has this ${label}`,
    builtin: { kind: 'must_be_unique' },
  }
}

/** "Smallest / Largest" (or Earliest / Latest): a value_in_range builtin. */
export function limitsRule(self: string, label: string, min: string, max: string): ValidationRule | null {
  const lo = min.trim()
  const hi = max.trim()
  if (!lo && !hi) return null
  const builtin: BuiltinValidation = { kind: 'value_in_range' }
  if (lo) builtin.min = { source: 'const', value: lo }
  if (hi) builtin.max = { source: 'const', value: hi }
  const message = lo && hi ? `${label} must be between ${lo} and ${hi}`
    : lo ? `${label} must be at least ${lo}` : `${label} must be at most ${hi}`
  return { type: 'builtin', name: `${self}${LIMITS_SUFFIX}`, severity: 'E', skip_if_blank: true, message, builtin }
}

export function findRule(validations: Validation[] | undefined, name: string): ValidationRule | undefined {
  return (validations || []).filter(isRule).find((v) => v.name === name)
}

export function limitsOf(validations: Validation[] | undefined, self: string): { min: string; max: string } {
  const r = findRule(validations, `${self}${LIMITS_SUFFIX}`)
  return { min: String(r?.builtin?.min?.value ?? ''), max: String(r?.builtin?.max?.value ?? '') }
}

// ── Checks before saving (the whole record) ──────────────────────────────

export type RecordCheckSentence =
  | { type: 'min_lines'; form: string; n: number }
  | { type: 'compare'; a: string; op: CompareOp; b: string }

export interface RecordCheck {
  sentence: RecordCheckSentence
  message: string
  severity: 'E' | 'W'
  name: string
}

export function recordCheckToRule(c: RecordCheck): ValidationRule {
  const base = { name: c.name, message: c.message, severity: c.severity as ValidationSeverity }
  if (c.sentence.type === 'min_lines') {
    return { ...base, type: 'builtin', builtin: { kind: 'child_form_has_entry', child_form: c.sentence.form, min_rows: Math.max(1, Math.round(c.sentence.n || 1)) } }
  }
  return { ...base, type: 'expression', expression: `${ref(c.sentence.a)} ${c.sentence.op} ${ref(c.sentence.b)}` }
}

export function ruleToRecordCheck(v: Validation): RecordCheck | null {
  if (!isRule(v) || v.disabled) return null
  const severity: 'E' | 'W' = v.severity === 'W' ? 'W' : 'E'
  if (v.type === 'builtin' && v.builtin?.kind === 'child_form_has_entry' && v.builtin.child_form) {
    return { sentence: { type: 'min_lines', form: String(v.builtin.child_form), n: Number(v.builtin.min_rows || 1) }, message: v.message || '', severity, name: v.name }
  }
  if (v.type === 'expression') {
    const m = String(v.expression || '').trim().match(new RegExp(`^\\{form\\.(${IDENT})\\}\\s*(>=|<=|==|>|<)\\s*\\{form\\.(${IDENT})\\}$`))
    if (m) return { sentence: { type: 'compare', a: m[1], op: m[2] as CompareOp, b: m[3] }, message: v.message || '', severity, name: v.name }
  }
  return null
}

export function recordCheckText(s: RecordCheckSentence, fieldLabel: (n: string) => string, formLabel: (f: string) => string): string {
  if (s.type === 'min_lines') return `There must be at least ${s.n} ${formLabel(s.form)} line${s.n === 1 ? '' : 's'}`
  return `${fieldLabel(s.a)} must be ${COMPARE_OPS.find((o) => o.op === s.op)?.label ?? s.op} ${fieldLabel(s.b)}`
}
